import { alpha, makeStyles, Theme } from '@material-ui/core/styles';
import { LAUNCHER_SIZE_PX } from 'src/modules/assistant/constants/widget';

const GLOW_ALPHA = 0.55;
const RING_ALPHA = 0.18;

export const useLauncherStyles = makeStyles((theme: Theme) => ({
    '@keyframes orbDrift': {
        '0%': { transform: 'rotate(0deg) scale(1)' },
        '50%': { transform: 'rotate(180deg) scale(1.13)' },
        '100%': { transform: 'rotate(360deg) scale(1)' },
    },
    '@keyframes caretBlink': {
        '0%, 49%': { opacity: 1 },
        '50%, 100%': { opacity: 0 },
    },
    '@keyframes statusPulse': {
        '0%, 100%': { transform: 'scale(1)', opacity: 0.8 },
        '50%': { transform: 'scale(1.12)', opacity: 1 },
    },
    header: {
        alignItems: 'center',
        borderBottom: '1px solid transparent',
        display: 'flex',
        flexShrink: 0,
        height: LAUNCHER_SIZE_PX,
    },
    headerOpen: { borderBottomColor: theme.palette.divider },
    launcher: {
        ...theme.typography.subtitle2,
        flex: '1 1 auto',
        gap: theme.spacing(1.25),
        height: '100%',
        justifyContent: 'space-between',
        minWidth: 0,
        padding: theme.spacing(0, 1, 0, 2.25),
        '&:hover': { backgroundColor: theme.palette.action.hover },
    },
    launcherCollapsed: {
        justifyContent: 'center',
        padding: 0,
    },
    launcherText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    // Keeps the status announced to screen readers while only the orb is shown.
    launcherTextHidden: {
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        position: 'absolute',
        whiteSpace: 'nowrap',
        width: 1,
    },
    // Same layer as the widget, so dialogs stay above the status bubble.
    statusPopper: { zIndex: theme.zIndex.drawer + 1 },
    orb: {
        background: `radial-gradient(circle at 30% 25%, ${theme.palette.common.white} 0%, ${theme.palette.primary.light} 30%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`,
        borderRadius: '50%',
        boxShadow: `0 0 12px ${alpha(theme.palette.primary.light, GLOW_ALPHA)}`,
        flexShrink: 0,
        height: 34,
        overflow: 'hidden',
        position: 'relative',
        width: 34,
        '&::after': {
            animation: '$orbDrift 6s ease-in-out infinite',
            background: `conic-gradient(from 20deg, transparent, ${alpha(
                theme.palette.common.white,
                0.8
            )}, transparent)`,
            content: '""',
            filter: 'blur(5px)',
            inset: -5,
            position: 'absolute',
        },
        '@media (prefers-reduced-motion: reduce)': {
            '&::after': { animation: 'none' },
        },
    },
    orbReady: {
        animation: '$statusPulse 1.8s ease-in-out 2',
        boxShadow: `0 0 0 5px ${alpha(
            theme.palette.success.main,
            RING_ALPHA
        )}, 0 0 18px ${alpha(theme.palette.success.main, GLOW_ALPHA)}`,
    },
    orbListening: {
        animation: '$statusPulse 1.2s ease-in-out infinite',
        background: `radial-gradient(circle at 30% 25%, ${theme.palette.common.white} 0%, ${theme.palette.error.light} 25%, ${theme.palette.error.main} 60%, ${theme.palette.error.dark} 100%)`,
        boxShadow: `0 0 0 4px ${alpha(
            theme.palette.error.main,
            RING_ALPHA
        )}, 0 0 18px ${alpha(theme.palette.error.main, GLOW_ALPHA)}`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    closeButton: { marginRight: theme.spacing(1) },
    typingCaret: {
        animation: '$caretBlink 700ms step-end infinite',
        backgroundColor: theme.palette.secondary.main,
        display: 'inline-block',
        height: 14,
        marginLeft: 2,
        verticalAlign: 'text-bottom',
        width: 2,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
}));
